declare global {
    interface Window {
        dataLayer: any[];
        gtag?: (...args: any[]) => void;
    }
}

type Ga4Params = Record<string, unknown>;

const DOWNLOAD_EXTENSIONS = /\.(pdf|docx?|xlsx?|pptx?|zip|csv|txt)$/i;
const MAX_LINK_TEXT_LENGTH = 100;

/**
 * Conversion tracking for GA4. The gtag base code lives in @layout.latte,
 * page_view is sent by gtag('config') on its own, this only adds the clicks
 * that count as a conversion on the site: phone, e-mail, outbound links
 * and document downloads.
 */
export default class Ga4Tracking {
    static init(): void {
        document.addEventListener('click', Ga4Tracking.handleClick, true);
    }

    static track(name: string, params: Ga4Params = {}): void {
        if (typeof window.gtag !== 'function') {
            return;
        }
        window.gtag('event', name, params);
    }

    private static handleClick = (event: Event): void => {
        const target = event.target as Element | null;
        const link = target?.closest('a');
        if (!link) {
            return;
        }

        const href = link.getAttribute('href') ?? '';
        if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
            return;
        }

        const linkText = (link.textContent ?? '').trim().slice(0, MAX_LINK_TEXT_LENGTH);

        if (href.startsWith('tel:')) {
            Ga4Tracking.track('contact_click', { method: 'phone', link_url: href, link_text: linkText });
            return;
        }

        if (href.startsWith('mailto:')) {
            Ga4Tracking.track('contact_click', { method: 'email', link_url: href, link_text: linkText });
            return;
        }

        let url: URL;
        try {
            url = new URL(href, window.location.href);
        } catch {
            return;
        }

        const download = DOWNLOAD_EXTENSIONS.exec(url.pathname);
        if (download) {
            Ga4Tracking.track('file_download', {
                file_name: url.pathname.split('/').pop() ?? '',
                file_extension: download[1].toLowerCase(),
                link_url: url.href,
                link_text: linkText,
            });
            return;
        }

        if (url.hostname !== window.location.hostname) {
            Ga4Tracking.track('outbound_click', {
                link_url: url.href,
                link_domain: url.hostname,
                link_text: linkText,
            });
        }
    };
}
